import { useEffect, useMemo, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getInventoryWithColors } from '../services/adminOrderInsightsService';

const { FiAlertTriangle, FiBox, FiCheckCircle, FiPackage, FiTrendingDown } = FiIcons;

function Metric({ icon, label, value, tone = '' }) {
  return (
    <div className={`inventory-metric ${tone}`}>
      <SafeIcon icon={icon} />
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function AdminInventory() {
  const [inventory, setInventory] = useState([]);
  const [state, setState] = useState('loading');

  useEffect(() => {
    getInventoryWithColors()
      .then((rows) => { setInventory(rows); setState('ready'); })
      .catch(() => setState('error'));
  }, []);

  const report = useMemo(() => {
    return inventory.reduce((s, item) => {
      const available = Math.max(0, item.quantity_on_hand - item.quantity_reserved - item.quantity_committed);
      s.onHand += item.quantity_on_hand;
      s.reserved += item.quantity_reserved;
      s.available += available;
      if (available === 0) s.outOfStock += 1;
      else if (available <= item.low_stock_threshold) s.lowStock += 1;
      else s.healthy += 1;
      return s;
    }, { onHand: 0, reserved: 0, available: 0, outOfStock: 0, lowStock: 0, healthy: 0 });
  }, [inventory]);

  const attentionItems = inventory.filter((item) => {
    const available = item.quantity_on_hand - item.quantity_reserved - item.quantity_committed;
    return available <= item.low_stock_threshold;
  });

  return (
    <div className="admin-overview-container">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Inventory Overview</h1>
          <p className="admin-page-subtitle">Stock levels across all products. Update quantities from Products.</p>
        </div>
      </div>

      {state === 'loading' && <div className="admin-state">Preparing the stock report…</div>}
      {state === 'error' && <div className="admin-state admin-error">Stock report could not be loaded.</div>}

      {state === 'ready' && (
        <>
          <div className="inventory-report-grid">
            <Metric icon={FiPackage} label="Products tracked" value={inventory.length} />
            <Metric icon={FiBox} label="Units on hand" value={report.onHand} />
            <Metric icon={FiCheckCircle} label="Units available" value={report.available} />
            <Metric icon={FiTrendingDown} label="Low stock" value={report.lowStock} tone="warning" />
            <Metric icon={FiAlertTriangle} label="Out of stock" value={report.outOfStock} tone="danger" />
            <Metric icon={FiCheckCircle} label="Healthy" value={report.healthy} tone="success" />
          </div>

          <div className="inventory-attention">
            <header>
              <div>
                <span className="eyebrow">Needs Attention</span>
                <h2>Low and Out-of-Stock Products</h2>
              </div>
              <p>{report.reserved} units reserved</p>
            </header>

            {attentionItems.length === 0 ? (
              <div className="admin-state">All products have healthy stock levels.</div>
            ) : (
              <div className="inventory-table">
                <div className="inventory-table-head">
                  <span>Product</span>
                  <span>On hand</span>
                  <span>Reserved</span>
                  <span>Available</span>
                  <span>Status</span>
                </div>
                {attentionItems.map((item) => {
                  const available = Math.max(0, item.quantity_on_hand - item.quantity_reserved - item.quantity_committed);
                  const color = item.variant?.color || item.variant?.attributes?.color || item.variant?.name;
                  return (
                    <div className="inventory-table-row" key={item.id}>
                      <strong>{item.product?.name || 'Unnamed'}{color ? ` - ${color}` : ''}</strong>
                      <span>{item.quantity_on_hand}</span>
                      <span>{item.quantity_reserved}</span>
                      <span>{available}</span>
                      <span className={`inventory-status ${available === 0 ? 'danger' : 'warning'}`}>
                        {available === 0 ? 'Out of stock' : 'Low stock'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default AdminInventory;